import L from "leaflet";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { format, subDays } from "date-fns";
import { parseListPayload } from "utils/paginationStats";
import { teacherDetailHref } from "utils/teacherRoutes";
import { readIsTeacher } from "utils/teacherFlags";
import { compactEstablishmentFilters } from "services/establishmentFilters";
import { getTeacherMapCard } from "services/teacherMapService";
import {
  buildTeacherAvatarHtml,
  escapeHtml,
  resolveProfilePic,
} from "utils/teacherAvatar";

export { buildTeacherAvatarHtml, escapeHtml };

export const BRIGHT_PALETTE = [
  "#FF6B6B",
  "#4ECDC4",
  "#FFD166",
  "#A29BFE",
  "#FF8C42",
  "#6EE7B7",
  "#8E44AD",
  "#00B4D8",
  "#F72585",
  "#43AA8B",
];

export const TEACHER_CATEGORIES = [
  { value: "", labelKey: "map.filter.category_all", fallback: "All categories" },
  { value: "1", labelKey: "teachers.category.all", fallback: "All teachers" },
  { value: "2", labelKey: "teachers.category.not_assigned", fallback: "Enrolled, not assigned" },
  { value: "3", labelKey: "teachers.category.assigned", fallback: "Enrolled and assigned" },
  { value: "4", labelKey: "teachers.category.assigned_with_attendance", fallback: "Assigned with attendance" },
];

export const VIEW_MODES = [
  { value: "markers", labelKey: "map.view.markers", fallback: "Markers" },
  { value: "cluster", labelKey: "map.view.cluster", fallback: "Clusters" },
  { value: "heatmap", labelKey: "map.view.heatmap", fallback: "Heatmap" },
  { value: "lines", labelKey: "map.view.lines", fallback: "Lines" },
];

/** Type de contrat — matches Teachers registry field `embedding` / `Embedding`. */
export const MAP_TEACHER_CONTRACT_TYPES = [
  "Fonctionnaire",
  "Contractuel d'État",
  "Contractuel",
  "Maître communautaire",
  "Bénévole",
  "Autre",
];

/** Fixed degree options (map filters). */
export const MAP_TEACHER_DEGREES = [
  "Aucun",
  "BEPCT/BEF",
  "CEFEN",
  "BAC",
  "DEUG/BTS",
  "Licence",
  "Master",
  "DIPES2/DEA",
  "Doctorat",
];

export function mapTeacherDegreeOptions() {
  return MAP_TEACHER_DEGREES.map((id) => ({ id, text: id }));
}

export function mapTeacherContractTypeOptions() {
  return MAP_TEACHER_CONTRACT_TYPES.map((id) => ({ id, text: id }));
}

export function shuffle(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export function makeOptions(data, ids, labels) {
  return (Array.isArray(data) ? data : []).map((item) => ({
    id: ids.map((key) => item[key]).find(Boolean),
    text: labels.map((key) => item[key]).find(Boolean),
  }));
}

export function normalizeEstablishment(row) {
  const lat = parseFloat(
    row?.latitude ?? row?.Latitude ?? row?.lat ?? row?.estLatitude ?? row?.EstLatitude
  );
  const lng = parseFloat(
    row?.longitude ?? row?.Longitude ?? row?.lng ?? row?.estLongitude ?? row?.EstLongitude
  );
  const attendanceRaw = row?.attendanceCount ?? row?.AttendanceCount;
  const attendanceCount = Number(attendanceRaw);
  return {
    id: row?.id ?? row?.Id ?? row?.estabId ?? row?.EstabId,
    name: row?.name ?? row?.Name ?? row?.establishment ?? row?.estName ?? row?.EstName ?? "",
    code: row?.code ?? row?.Code ?? "",
    type: row?.type ?? row?.Type ?? row?.estType ?? row?.EstType ?? "",
    status: row?.status ?? row?.Status ?? row?.establishmentStatus ?? row?.EstablishmentStatus ?? "",
    latitude: Number.isFinite(lat) ? lat : null,
    longitude: Number.isFinite(lng) ? lng : null,
    attendanceCount: Number.isFinite(attendanceCount) ? attendanceCount : 0,
  };
}

export function distanceBetweenCoordinates(lat1, lon1, lat2, lon2) {
  const n = (v) => {
    const x = parseFloat(v);
    return Number.isFinite(x) ? x : NaN;
  };
  const a = n(lat1);
  const b = n(lon1);
  const c = n(lat2);
  const d = n(lon2);
  if ([a, b, c, d].some((x) => Number.isNaN(x))) return null;
  const earthRadius = 6371;
  const latFrom = (a * Math.PI) / 180;
  const lonFrom = (b * Math.PI) / 180;
  const latTo = (c * Math.PI) / 180;
  const lonTo = (d * Math.PI) / 180;
  const latDelta = latTo - latFrom;
  const lonDelta = lonTo - lonFrom;
  const angle =
    2 *
    Math.asin(
      Math.sqrt(
        Math.sin(latDelta / 2) ** 2 +
          Math.cos(latFrom) * Math.cos(latTo) * Math.sin(lonDelta / 2) ** 2
      )
    );
  return angle * earthRadius;
}

export function rowDistanceKm(row) {
  const distanceMeters = Number(row?.distanceMeters ?? row?.DistanceMeters);
  if (Number.isFinite(distanceMeters) && distanceMeters >= 0) return distanceMeters / 1000;
  const estLat = row?.estLatitude ?? row?.EstLatitude;
  const estLon = row?.estLongitude ?? row?.EstLongitude;
  const attLat = row?.attLatitude ?? row?.AttLatitude;
  const attLon = row?.attLongitude ?? row?.AttLongitude;
  const km = distanceBetweenCoordinates(estLat, estLon, attLat, attLon);
  return Number.isFinite(km) ? km : null;
}

export function getViewportSnapshot(map) {
  const bounds = map?.getBounds?.();
  const zoom = map?.getZoom?.();
  if (!bounds?.isValid?.() || !Number.isFinite(zoom)) return null;
  return {
    zoom,
    north: bounds.getNorth(),
    south: bounds.getSouth(),
    east: bounds.getEast(),
    west: bounds.getWest(),
    centerLat: bounds.getCenter().lat,
    centerLng: bounds.getCenter().lng,
    latSpan: Math.max(Math.abs(bounds.getNorth() - bounds.getSouth()), 0.000001),
    lngSpan: Math.max(Math.abs(bounds.getEast() - bounds.getWest()), 0.000001),
  };
}

export function hasMeaningfulViewportChange(previous, next, threshold = 0.12) {
  if (!previous || !next) return true;
  if (previous.zoom !== next.zoom) return true;
  const latShift = Math.abs(previous.centerLat - next.centerLat);
  const lngShift = Math.abs(previous.centerLng - next.centerLng);
  return latShift >= previous.latSpan * threshold || lngShift >= previous.lngSpan * threshold;
}

export function getViewportCacheKey(snapshot, filters = {}) {
  if (!snapshot) return "";
  const normalizedFilters = Object.keys(filters)
    .sort()
    .reduce((result, key) => {
      const value = filters[key];
      if (value !== "" && value != null) result[key] = String(value);
      return result;
    }, {});
  return JSON.stringify({
    z: snapshot.zoom,
    n: Number(snapshot.north.toFixed(2)),
    s: Number(snapshot.south.toFixed(2)),
    e: Number(snapshot.east.toFixed(2)),
    w: Number(snapshot.west.toFixed(2)),
    filters: normalizedFilters,
  });
}

export function setBoundedMapCache(cache, key, value, limit = 12) {
  if (!cache || !key) return;
  cache.delete(key);
  cache.set(key, value);
  while (cache.size > limit) cache.delete(cache.keys().next().value);
}

export function isWithinRadius(row, radiusMeters) {
  const km = rowDistanceKm(row);
  if (km === null) return null;
  const radiusKm = Number(radiusMeters) / 1000;
  if (!Number.isFinite(radiusKm) || radiusKm <= 0) return null;
  return km <= radiusKm;
}

export function getTeacherId(row) {
  return row?.id ?? row?.Id ?? row?.teacherId ?? row?.TeacherId;
}

export function getTeacherReg(row) {
  return String(row?.regNumber ?? row?.RegNumber ?? "").trim();
}

export function getTeacherName(row) {
  const first = row?.name ?? row?.Name ?? "";
  const last = row?.preName ?? row?.PreName ?? row?.lastName ?? "";
  return [first, last].filter(Boolean).join(" ") || "—";
}

export function computeMedian(values) {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

export function getAttendanceTier(count, establishments) {
  const n = Number(count) || 0;
  const counts = establishments.map((e) => Number(e.attendanceCount) || 0);
  const unique = [...new Set(counts)];
  if (unique.length <= 1) return n > 0 ? "high" : "low";
  const threshold = computeMedian(counts);
  return n > threshold ? "high" : "low";
}

export function normalizeFilterText(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

export function buildCategoryLookup(rows) {
  const ids = new Set();
  const regs = new Set();
  (rows || []).forEach((row) => {
    const id = getTeacherId(row);
    const reg = getTeacherReg(row);
    if (id != null && id !== "") ids.add(String(id));
    if (reg) regs.add(reg.toLowerCase());
  });
  return { ids, regs };
}

export function teacherMatchesCategory(row, lookup) {
  if (!lookup) return true;
  const id = getTeacherId(row);
  const reg = getTeacherReg(row).toLowerCase();
  if (id != null && lookup.ids.has(String(id))) return true;
  if (reg && lookup.regs.has(reg)) return true;
  return false;
}

export function collectTeacherFilterOptions(rows) {
  const genders = new Set(["Homme", "Femme"]);
  const degrees = new Set();

  (rows || []).forEach((row) => {
    const g = String(row?.gender ?? row?.Gender ?? "").trim();
    const d = String(row?.degree ?? row?.Degree ?? "").trim();
    if (g) genders.add(g);
    if (d) degrees.add(d);
  });

  const toOpts = (set) =>
    Array.from(set)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((text) => ({ id: text, text }));

  return {
    genders: toOpts(genders),
    degrees: toOpts(degrees),
  };
}

export function fieldMatchesFilter(filterValue, rowValue) {
  const filter = normalizeFilterText(filterValue);
  if (!filter) return true;
  const value = normalizeFilterText(rowValue);
  if (!value) return false;
  if (value === filter) return true;
  return value.includes(filter) || filter.includes(value);
}

export function getTeacherDegree(row, profileLookup) {
  const id = getTeacherId(row);
  const profile = id != null && profileLookup?.get ? profileLookup.get(String(id)) : null;
  return String(row?.degree ?? row?.Degree ?? profile?.degree ?? "").trim();
}

export function getTeacherContractType(row, profileLookup) {
  const id = getTeacherId(row);
  const profile = id != null && profileLookup?.get ? profileLookup.get(String(id)) : null;
  return String(
    profile?.contractType ??
      row?.embedding ??
      row?.Embedding ??
      row?.contractType ??
      row?.ContractType ??
      ""
  ).trim();
}

export function filterTeacherRows(rows, filters, categoryLookup, profileLookup) {
  const gender = filters.gender;
  const degree = filters.degree;
  const contractType = filters.contractType;
  const radiusMeters = Number(filters.radiusMeters);
  const radiusFilter = filters.radiusFilter;

  return (rows || []).filter((row) => {
    if (categoryLookup && !teacherMatchesCategory(row, categoryLookup)) return false;

    if (row?._serverFiltered !== true) {
      if (!fieldMatchesFilter(gender, row?.gender ?? row?.Gender)) return false;
      if (!fieldMatchesFilter(degree, getTeacherDegree(row, profileLookup))) return false;
      if (!fieldMatchesFilter(contractType, getTeacherContractType(row, profileLookup))) return false;
    }
    if (radiusFilter === "in") {
      const within = isWithinRadius(row, radiusMeters);
      if (within !== true) return false;
    } else if (radiusFilter === "out") {
      const within = isWithinRadius(row, radiusMeters);
      if (within !== false) return false;
    }

    return true;
  });
}

export function dedupeTeachers(rows) {
  const seen = new Set();
  const out = [];
  (rows || []).forEach((row) => {
    const key = [
      getTeacherId(row),
      getTeacherReg(row),
      row?.attLatitude ?? row?.AttLatitude,
      row?.attLongitude ?? row?.AttLongitude,
      row?.attendDateTime ?? row?.AttendDateTime,
    ].join("|");
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  return out;
}

export async function fetchTeachersForEstablishments(
  api,
  estabIds,
  attendDate,
  pageSize = 500,
  establishmentFilters = {}
) {
  const uniqueIds = [...new Set((estabIds || []).filter(Boolean).map(String))];
  if (!uniqueIds.length) return [];

  const batchSize = 6;
  const merged = [];

  for (let i = 0; i < uniqueIds.length; i += batchSize) {
    const batch = uniqueIds.slice(i, i + batchSize);
    const chunk = await Promise.all(
      batch.map(async (estabId) => {
        try {
          const params = {
            EstabId: estabId,
            pageNumber: 1,
            pageSize,
            ...compactEstablishmentFilters(establishmentFilters),
          };
          if (attendDate) params.AttendDate = format(new Date(attendDate), "yyyy-MM-dd");
          const token = localStorage.getItem("tk446655440000");
          const res = await api.get("/Teacher/scoresummary", {
            params,
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          const list = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.data)
              ? res.data.data
              : [];
          return list;
        } catch {
          return [];
        }
      })
    );
    merged.push(...chunk.flat());
  }

  return dedupeTeachers(merged);
}

export function countRadiusBuckets(teachers, radiusMeters) {
  let inRadius = 0;
  let outRadius = 0;
  let unknown = 0;
  (teachers || []).forEach((row) => {
    const within = isWithinRadius(row, radiusMeters);
    if (within === true) inRadius += 1;
    else if (within === false) outRadius += 1;
    else unknown += 1;
  });
  return { inRadius, outRadius, unknown };
}

export function buildTeacherPopup(row, labels) {
  const {
    viewProfileLabel,
    viewHistoryLabel,
    statusLabel,
    genderLabel,
    degreeLabel,
    distanceLabel,
    radiusLabel,
    inRadiusLabel,
    outRadiusLabel,
    radiusMeters,
  } = labels;
  const name = escapeHtml(getTeacherName(row));
  const status = escapeHtml(row?.status ?? row?.Status ?? "—");
  const gender = escapeHtml(row?.gender ?? row?.Gender ?? "—");
  const degree = escapeHtml(row?.degree ?? row?.Degree ?? "—");
  const imei = escapeHtml(row?.imei ?? row?.Imei ?? "—");
  const estab = escapeHtml(row?.estName ?? row?.EstName ?? "—");
  const km = rowDistanceKm(row);
  const distanceLine =
    km !== null
      ? `<div class="map-popup-meta">${escapeHtml(distanceLabel)}: ${km.toFixed(2)} km</div>`
      : "";
  const within = isWithinRadius(row, radiusMeters);
  let radiusBadge = "";
  if (within === true) {
    radiusBadge = `<span class="map-popup-badge map-popup-badge--in">${escapeHtml(inRadiusLabel)}</span>`;
  } else if (within === false) {
    radiusBadge = `<span class="map-popup-badge map-popup-badge--out">${escapeHtml(outRadiusLabel)}</span>`;
  }
  const id = getTeacherId(row);
  const historyLink = id
    ? `<a href="/histoire-des-enseignants/${encodeURIComponent(id)}" class="map-popup-btn map-popup-btn--secondary">${escapeHtml(
        viewHistoryLabel
      )}</a>`
    : "";
  const profileHref = teacherDetailHref(row);
  const profileLink = profileHref
    ? `<a href="${profileHref}" class="map-popup-btn">${escapeHtml(
        viewProfileLabel
      )}</a>`
    : "";

  const avatar = buildTeacherAvatarHtml(row, 36);
  return `<div class="map-popup-header">${avatar}<div class="map-popup-header-text"><div class="map-popup-title">${name}${radiusBadge}</div></div></div>
    <div class="map-popup-meta">${escapeHtml(statusLabel)}: ${status}</div>
    <div class="map-popup-meta">${escapeHtml(genderLabel)}: ${gender}</div>
    <div class="map-popup-meta">${escapeHtml(degreeLabel)}: ${degree}</div>
    <div class="map-popup-meta">${escapeHtml(radiusLabel)}: ${escapeHtml(estab)}</div>
    ${distanceLine}
    <div class="map-popup-meta">IMEI: ${imei}</div>
    <div class="map-popup-actions">${historyLink}${profileLink}</div>`;
}

export function bindLazyTeacherPopup(marker, row, renderPopup) {
  if (!marker || typeof renderPopup !== "function") return;
  marker.bindPopup(renderPopup(row), { className: "map-custom-popup", maxWidth: 280 });
  let loaded = false;
  let pending = null;
  marker.on("popupopen", () => {
    if (loaded || pending) return;
    const teacherId = getTeacherId(row);
    const checkInId = row?.checkInId ?? row?.CheckInId;
    if (!teacherId) return;
    pending = getTeacherMapCard(teacherId, checkInId)
      .then((card) => {
        if (!card) return;
        loaded = true;
        marker.setPopupContent(renderPopup({ ...row, ...card }));
      })
      .catch(() => {
        marker.setPopupContent(renderPopup(row));
      })
      .finally(() => {
        pending = null;
      });
  });
}

const TEACHER_POPUP_PREVIEW_LIMIT = 4;

function viewProfileLink(label, row) {
  if (!row) return "";
  const href = teacherDetailHref(row);
  if (!href || href === "/enseignantes") return "";
  return `<div class="map-popup-actions"><a href="${href}" class="map-popup-btn">${escapeHtml(
    label
  )}</a></div>`;
}

function renderTeacherLinks(teachers, { expanded, viewMoreLabel, showLessLabel }) {
  const total = teachers.length;
  const visible = expanded ? teachers : teachers.slice(0, TEACHER_POPUP_PREVIEW_LIMIT);
  const hiddenCount = total - TEACHER_POPUP_PREVIEW_LIMIT;

  const items = visible
    .map((row) => {
      const id = getTeacherId(row);
      const name = escapeHtml(getTeacherName(row));
      const status = escapeHtml(row?.status ?? row?.Status ?? "");
      const statusPart = status ? `<span class="map-popup-status">${status}</span>` : "";
      const avatar = buildTeacherAvatarHtml(row, 24);
      if (!id) {
        return `<div class="map-popup-teacher">${avatar}<span class="map-popup-teacher-name">${name}${statusPart}</span></div>`;
      }
      const href = teacherDetailHref(row);
      return `<div class="map-popup-teacher">${avatar}<span class="map-popup-teacher-name"><a href="${href}">${name}</a>${statusPart}</span></div>`;
    })
    .join("");

  let toggle = "";
  if (!expanded && hiddenCount > 0) {
    toggle = `<button type="button" class="map-popup-view-more" data-map-view-more="1">${escapeHtml(
      viewMoreLabel.replace("{{count}}", String(hiddenCount))
    )}</button>`;
  } else if (expanded && total > TEACHER_POPUP_PREVIEW_LIMIT) {
    toggle = `<button type="button" class="map-popup-show-less" data-map-show-less="1">${escapeHtml(
      showLessLabel
    )}</button>`;
  }

  return `${items}${toggle}`;
}

export function buildEstablishmentPopup(estab, teachers, labels, expanded = false) {
  const {
    loadingTeachers,
    noTeachersLabel,
    teachersLabel,
    clickHintLabel,
    viewProfileLabel,
    viewMoreLabel,
    showLessLabel,
    attendanceLabel,
  } = labels;
  const header = `<div class="map-popup-title">${escapeHtml(estab.name)}</div>`;
  const typeLine = estab.type ? `<div class="map-popup-meta">Type: ${escapeHtml(estab.type)}</div>` : "";
  const attendanceLine = `<div class="map-popup-meta">${escapeHtml(attendanceLabel || "Attendance")}: ${Number(estab.attendanceCount ?? 0)}</div>`;

  if (loadingTeachers) {
    return `${header}${typeLine}${attendanceLine}<div class="map-popup-loading">${escapeHtml(teachersLabel)}…</div>`;
  }

  if (!teachers.length) {
    const hint = clickHintLabel || noTeachersLabel;
    return `${header}${typeLine}${attendanceLine}<div class="map-popup-empty">${escapeHtml(hint)}</div>`;
  }

  const links = renderTeacherLinks(teachers, {
    expanded,
    viewMoreLabel: viewMoreLabel || "View {{count}} more",
    showLessLabel: showLessLabel || "Show less",
  });

  const singleTeacherLink = teachers.length === 1 ? viewProfileLink(viewProfileLabel, teachers[0]) : "";

  return `${header}${typeLine}${attendanceLine}<div class="map-popup-section">${escapeHtml(teachersLabel)} (${teachers.length})</div>${links}${singleTeacherLink}`;
}

export function bindEstablishmentPopupHandlers(container, { onExpand, onCollapse }) {
  if (!container) return;
  const viewMore = container.querySelector("[data-map-view-more]");
  const showLess = container.querySelector("[data-map-show-less]");
  if (viewMore) {
    viewMore.onclick = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      onExpand?.();
    };
  }
  if (showLess) {
    showLess.onclick = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      onCollapse?.();
    };
  }
}

export function bindEstablishmentNameTooltip(marker, name) {
  marker.bindTooltip(escapeHtml(name || "—"), {
    direction: "top",
    offset: [0, -48],
    opacity: 1,
    className: "map-estab-tooltip",
  });
}

export function createTeacherDivIcon(kind = "default") {
  const colors = {
    in: "#10b981",
    out: "#f97316",
    compare: "#8b5cf6",
    default: "#667eea",
  };
  const bg = colors[kind] || colors.default;
  return L.divIcon({
    className: "map-teacher-dot-wrap",
    html: `<span class="map-teacher-dot map-teacher-dot--${kind}" style="background:${bg}"></span>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -8],
  });
}

export function createClusterDivIcon(count) {
  const size = count > 99 ? 44 : count > 9 ? 38 : 32;
  return L.divIcon({
    className: "map-cluster-icon-wrap",
    html: `<span class="map-cluster-icon">${count > 999 ? "999+" : count}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

export function groupTeachersForCluster(rows, precision = 2) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const lat = parseFloat(row?.attLatitude ?? row?.AttLatitude);
    const lng = parseFloat(row?.attLongitude ?? row?.AttLongitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const key = `${lat.toFixed(precision)},${lng.toFixed(precision)}`;
    if (!map.has(key)) map.set(key, { lat, lng, rows: [] });
    map.get(key).rows.push(row);
  });
  return Array.from(map.values());
}

export function groupTeachersForHeatmap(rows, cellSize = 0.015) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const lat = parseFloat(row?.attLatitude ?? row?.AttLatitude);
    const lng = parseFloat(row?.attLongitude ?? row?.AttLongitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const latKey = Math.round(lat / cellSize) * cellSize;
    const lngKey = Math.round(lng / cellSize) * cellSize;
    const key = `${latKey},${lngKey}`;
    if (!map.has(key)) map.set(key, { lat: latKey, lng: lngKey, count: 0 });
    map.get(key).count += 1;
  });
  return Array.from(map.values());
}

export function getMapFitPadding({ sidePanelOpen = false, filtersExpanded = false } = {}) {
  const w = typeof window !== "undefined" ? window.innerWidth : 1024;
  let toolbarTop = w < 640 ? 118 : 96;
  if (filtersExpanded) toolbarTop = w < 640 ? 210 : 168;
  const side = w < 640 ? 52 : 60;
  const bottom = w < 640 ? 88 : 56;
  const rightPanel = sidePanelOpen && w >= 900 ? 340 : 0;
  return {
    paddingTopLeft: L.point(side, toolbarTop),
    paddingBottomRight: L.point(side + rightPanel, bottom),
  };
}

export function boundsFromTeachers(teachers, estab) {
  const bounds = L.latLngBounds([]);
  const extend = (lat, lng) => {
    if (Number.isFinite(lat) && Number.isFinite(lng)) bounds.extend([lat, lng]);
  };

  if (estab?.latitude != null && estab?.longitude != null) {
    extend(estab.latitude, estab.longitude);
  }

  (teachers || []).forEach((row) => {
    extend(parseFloat(row?.attLatitude ?? row?.AttLatitude), parseFloat(row?.attLongitude ?? row?.AttLongitude));
    extend(parseFloat(row?.estLatitude ?? row?.EstLatitude), parseFloat(row?.estLongitude ?? row?.EstLongitude));
  });

  if (!bounds.isValid()) return null;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const latSpan = Math.abs(ne.lat - sw.lat);
  const lngSpan = Math.abs(ne.lng - sw.lng);
  if (latSpan < 0.002 || lngSpan < 0.002) {
    const center = bounds.getCenter();
    const pad = 0.004;
    bounds.extend([center.lat + pad, center.lng + pad]);
    bounds.extend([center.lat - pad, center.lng - pad]);
  }

  return bounds;
}

export function fitMapToBounds(map, bounds, { sidePanelOpen = false, filtersExpanded = false, maxZoom = 15 } = {}) {
  if (!bounds?.isValid?.() || !map) return;
  const { paddingTopLeft, paddingBottomRight } = getMapFitPadding({ sidePanelOpen, filtersExpanded });
  map.flyToBounds(bounds, {
    paddingTopLeft,
    paddingBottomRight,
    maxZoom,
    duration: 1,
    easeLinearity: 0.25,
  });
}

export function fitMapToAllEstablishments(map, establishments, options = {}) {
  const bounds = [];
  (establishments || []).forEach((estab) => {
    if (estab.latitude != null && estab.longitude != null) {
      bounds.push([estab.latitude, estab.longitude]);
    }
  });
  if (!bounds.length || !map) return;
  fitMapToBounds(map, L.latLngBounds(bounds), { ...options, maxZoom: 15 });
}

export function fadeInMarker(marker, delayMs = 0) {
  window.setTimeout(() => {
    marker.setOpacity(0);
    let opacity = 0;
    const step = () => {
      opacity = Math.min(1, opacity + 0.12);
      marker.setOpacity(opacity);
      if (opacity < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, delayMs);
}

export function exportTeacherRegistryToExcel(rows, filename = "teachers-export.xlsx", attendanceMeta) {
  const data = (rows || []).map((row) => {
    const id = String(row?.id ?? row?.Id ?? "");
    const att = attendanceMeta?.get?.(id);
    const lastCheckIn = att?.lastCheckIn ?? row?.lastCheckIn ?? row?.LastCheckIn;
    const inRadius = att?.inRadius ?? row?.isWithinRadius ?? row?.IsWithinRadius;
    return {
      RegNumber: row?.regNumber ?? row?.RegNumber ?? "",
      Name: [row?.name, row?.preName].filter(Boolean).join(" "),
      Gender: row?.gender ?? row?.Gender ?? "",
      Degree: row?.degree ?? row?.Degree ?? "",
      Status: row?.status ?? row?.Status ?? "",
      Recruiter: row?.recruiter ?? row?.Recruiter ?? "",
      IsAllocated: row?.isAllocated ?? row?.IsAllocated ?? "",
      HasAttendance: row?.hasAttendance ?? row?.HasAttendance ?? "",
      LastCheckIn: lastCheckIn ? String(lastCheckIn).replace("T", " ").slice(0, 19) : "",
      InRadius: inRadius === true ? "Yes" : inRadius === false ? "No" : "",
    };
  });
  const sheet = XLSX.utils.json_to_sheet(data);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "Teachers");
  const buffer = XLSX.write(book, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buffer], { type: "application/octet-stream" }), filename);
}

export function exportTeachersToExcel(teachers, filename = "teacher-map-export.xlsx", radiusMeters = 500) {
  const rows = (teachers || []).map((row) => {
    const km = rowDistanceKm(row);
    const within = km !== null ? isWithinRadius(row, radiusMeters) : null;
    return {
      Name: getTeacherName(row),
      RegNumber: getTeacherReg(row),
      Gender: row?.gender ?? row?.Gender ?? "",
      Degree: row?.degree ?? row?.Degree ?? "",
      Status: row?.status ?? row?.Status ?? "",
      CompareDate: row?._compareSlot === "compare" ? "Yes" : "",
      Establishment: row?.estName ?? row?.EstName ?? "",
      AttLatitude: row?.attLatitude ?? row?.AttLatitude ?? "",
      AttLongitude: row?.attLongitude ?? row?.AttLongitude ?? "",
      EstLatitude: row?.estLatitude ?? row?.EstLatitude ?? "",
      EstLongitude: row?.estLongitude ?? row?.EstLongitude ?? "",
      DistanceKm: km !== null ? km.toFixed(2) : "",
      InRadius: within === true ? "Yes" : within === false ? "No" : "",
      RadiusMeters: radiusMeters,
      IMEI: row?.imei ?? row?.Imei ?? "",
      AttendDateTime: row?.attendDateTime ?? row?.AttendDateTime ?? "",
    };
  });
  const sheet = XLSX.utils.json_to_sheet(rows);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "Teachers");
  const buffer = XLSX.write(book, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buffer], { type: "application/octet-stream" }), filename);
}

export function defaultSummaryDates() {
  const end = new Date();
  const start = new Date(end);
  start.setMonth(start.getMonth() - 1);
  return {
    startDate: format(start, "yyyy-MM-dd"),
    endDate: format(end, "yyyy-MM-dd"),
  };
}

export const MAP_CONTEXT = {
  IDLE: "idle",
  TEACHER_TIMELINE: "teacher-timeline",
  SCHOOL_OVERVIEW: "school-overview",
  AREA_OVERVIEW: "area-overview",
};

export function getAttendDateKey(row) {
  const raw = row?.attendDateTime ?? row?.AttendDateTime ?? row?.attendDate ?? row?.AttendDate ?? "";
  if (!raw) return "";
  return String(raw).slice(0, 10);
}

export function filterHistoryByDateRange(rows, startDate, endDate) {
  const start = startDate ? String(startDate).slice(0, 10) : "";
  const end = endDate ? String(endDate).slice(0, 10) : "";
  if (!start && !end) return rows || [];

  return (rows || []).filter((row) => {
    const key = getAttendDateKey(row);
    if (!key) return false;
    if (start && key < start) return false;
    if (end && key > end) return false;
    return true;
  });
}

export function groupHistoryByDay(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const key = getAttendDateKey(row);
    if (!key) return;
    if (!map.has(key)) map.set(key, { date: key, rows: [], count: 0 });
    const group = map.get(key);
    group.rows.push(row);
    group.count += 1;
  });
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
}

export function buildTeacherRosterOptions(rows) {
  const seen = new Map();
  (rows || []).forEach((row) => {
    const id = getTeacherId(row);
    if (id == null || id === "" || seen.has(String(id))) return;
    const name = getTeacherName(row);
    const reg = getTeacherReg(row);
    seen.set(String(id), {
      id: String(id),
      text: [reg, name].filter(Boolean).join(" — ") || name || String(id),
      name,
      reg,
      profilePic: resolveProfilePic(row) || undefined,
    });
  });
  return Array.from(seen.values()).sort((a, b) => a.text.localeCompare(b.text));
}

function normalizeEstabLabel(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function addRosterEntry(
  roster,
  profiles,
  id,
  name,
  reg,
  degree = "",
  contractType = "",
  profilePic = "",
  status = "",
  isTeacher = ""
) {
  if (id == null || id === "") return;
  const key = String(id);
  const cleanName = String(name ?? "").trim();
  const cleanReg = String(reg ?? "").trim();
  const cleanDegree = String(degree ?? "").trim();
  const cleanContract = String(contractType ?? "").trim();
  const cleanPic = resolveProfilePic(profilePic) || "";

  if (!roster.has(key)) {
    roster.set(key, {
      id: key,
      text: [cleanReg, cleanName].filter(Boolean).join(" — ") || cleanName || key,
      name: cleanName,
      reg: cleanReg,
      profilePic: cleanPic || undefined,
    });
  } else if (cleanPic && !roster.get(key).profilePic) {
    roster.get(key).profilePic = cleanPic;
  }

  const existing = profiles.get(key) || {};
  profiles.set(key, {
    name: cleanName || existing.name || "",
    reg: cleanReg || existing.reg || "",
    degree: cleanDegree || existing.degree || "",
    contractType: cleanContract || existing.contractType || "",
    status: String(status ?? "").trim() || existing.status || "",
    isTeacher:
      isTeacher !== "" && isTeacher != null
        ? readIsTeacher(isTeacher)
        : existing.isTeacher ?? "",
    profilePic: cleanPic || existing.profilePic || "",
  });
}

async function mergeTeacherRegistryProfiles(apiClient, roster, profiles, headers) {
  const ids = Array.from(roster.keys());
  if (!ids.length) return;

  try {
    const { data } = await apiClient.get("/Teacher/list", {
      params: { pageNumber: 1, pageSize: 5000, category: 1 },
      headers,
    });
    const idSet = new Set(ids.map(String));
    parseListPayload(data).forEach((row) => {
      const id = getTeacherId(row);
      if (id == null || !idSet.has(String(id))) return;
      const key = String(id);
      const existing = profiles.get(key) || {};
      const pic = resolveProfilePic(row) || existing.profilePic || "";
      profiles.set(key, {
        name: getTeacherName(row) || existing.name || "",
        reg: getTeacherReg(row) || existing.reg || "",
        degree: String(row?.degree ?? row?.Degree ?? existing.degree ?? "").trim(),
        contractType: String(row?.embedding ?? row?.Embedding ?? existing.contractType ?? "").trim(),
        status: String(row?.status ?? row?.Status ?? existing.status ?? "").trim(),
        isTeacher:
          row?.isTeacher != null || row?.IsTeacher != null
            ? readIsTeacher(row?.isTeacher ?? row?.IsTeacher)
            : existing.isTeacher ?? "",
        profilePic: pic,
      });
      const rosterEntry = roster.get(key);
      if (rosterEntry && pic && !rosterEntry.profilePic) {
        rosterEntry.profilePic = pic;
      }
    });
  } catch {
    /* assignment/report profiles remain */
  }
}

function mergeCheckInsIntoRoster(roster, profiles, checkIns) {
  buildTeacherRosterOptions(checkIns).forEach((opt) => {
    const profile = profiles.get(String(opt.id));
    addRosterEntry(
      roster,
      profiles,
      opt.id,
      opt.name,
      opt.reg,
      profile?.degree ?? "",
      profile?.contractType ?? "",
      profile?.profilePic ?? opt.profilePic ?? "",
      profile?.status ?? "",
      profile?.isTeacher ?? ""
    );
  });
}

/** Assigned teachers at an establishment (not limited to a single check-in day). */
export async function fetchAssignedTeachersForEstablishment(
  apiClient,
  estabId,
  {
    estabName = "",
    startDate = "",
    endDate = "",
    checkIns = null,
    enrichFromRegistry = false,
  } = {}
) {
  if (!estabId) return { roster: [], profiles: new Map() };

  const token = localStorage.getItem("tk446655440000");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const roster = new Map();
  const profiles = new Map();
  const end = endDate ? String(endDate).slice(0, 10) : format(new Date(), "yyyy-MM-dd");
  const start = startDate ? String(startDate).slice(0, 10) : format(subDays(new Date(end), 365), "yyyy-MM-dd");
  const safeStart = start <= end ? start : end;
  const safeEnd = start <= end ? end : start;
  const targetName = normalizeEstabLabel(estabName);

  // Prefer already-fetched scoresummary rows so the UI can paint immediately.
  if (Array.isArray(checkIns) && checkIns.length) {
    mergeCheckInsIntoRoster(roster, profiles, checkIns);
  }

  try {
    const { data } = await apiClient.get("/Teacher/establishmentattendancereport", {
      params: {
        estabId,
        startDate: safeStart,
        endDate: safeEnd,
        radius: 500,
        pageNumber: 1,
        pageSize: 500,
      },
      headers,
    });
    const teachers = Array.isArray(data?.teachers) ? data.teachers : [];
    teachers.forEach((row) => {
      addRosterEntry(
        roster,
        profiles,
        row.teacherId ?? row.id ?? row.Id,
        row.teacherName ?? getTeacherName(row),
        row.regNumber ?? row.RegNumber ?? getTeacherReg(row),
        row.degree ?? row.Degree ?? row.teacherDegree ?? "",
        row.embedding ?? row.Embedding ?? "",
        row.profilePic ?? row.ProfilePic ?? "",
        row.status ?? row.Status ?? row.teacherStatus ?? row.TeacherStatus ?? "",
        row.isTeacher ?? row.IsTeacher ?? ""
      );
    });
  } catch {
    /* fallback sources below */
  }

  // Only hit Assignment/summary when report + check-ins did not yield a roster.
  if (!roster.size) {
    try {
      const fetchSummary = async (params) => {
        const { data } = await apiClient.get("/Assignment/summary", { params, headers });
        return parseListPayload(data);
      };

      let rows = await fetchSummary({ EstabId: estabId, pageNumber: 1, pageSize: 500 });
      if (!rows.length) rows = await fetchSummary({ estabId, pageNumber: 1, pageSize: 500 });

      rows.forEach((row) => {
        const rowEstabId = row.estabId ?? row.EstabId ?? row.estId ?? row.EstId;
        const matchesId = rowEstabId != null && String(rowEstabId) === String(estabId);
        const rowEstName = normalizeEstabLabel(row.estName ?? row.EstName ?? row.establishment ?? "");
        const matchesName = targetName && rowEstName && rowEstName === targetName;
        if (!matchesId && !matchesName) return;

        addRosterEntry(
          roster,
          profiles,
          row.teacherId ??
            row.TeacherId ??
            row.asignId ??
            row.AsignId ??
            row.assignId ??
            row.AssignId,
          getTeacherName(row),
          getTeacherReg(row),
          row.degree ?? row.Degree ?? "",
          row.embedding ?? row.Embedding ?? "",
          row.profilePic ?? row.ProfilePic ?? "",
          row.status ?? row.Status ?? row.teacherStatus ?? row.TeacherStatus ?? "",
          row.isTeacher ?? row.IsTeacher ?? ""
        );
      });
    } catch {
      /* fallback below */
    }
  }

  // Avoid a duplicate scoresummary when the caller already supplied check-ins.
  if (!Array.isArray(checkIns)) {
    try {
      const fetchedCheckIns = await fetchTeachersForEstablishments(apiClient, [estabId], "", 500);
      mergeCheckInsIntoRoster(roster, profiles, fetchedCheckIns);
    } catch {
      /* ignore */
    }
  }

  // Full /Teacher/list (5000) is too expensive for map interactions — opt in only.
  if (enrichFromRegistry) {
    await mergeTeacherRegistryProfiles(apiClient, roster, profiles, headers);
  }

  return {
    roster: Array.from(roster.values()).sort((a, b) => a.text.localeCompare(b.text)),
    profiles,
  };
}

export async function fetchTeacherHistory(
  apiClient,
  teacherId,
  { startDate = "", endDate = "", pageNumber = 1, pageSize = 500 } = {}
) {
  if (!teacherId) return [];
  const token = localStorage.getItem("tk446655440000");
  const params = { Id: teacherId, pageNumber, pageSize };
  const start = startDate ? String(startDate).slice(0, 10) : "";
  const end = endDate ? String(endDate).slice(0, 10) : "";
  if (start) {
    params.StartDate = start;
    params.startDate = start;
  }
  if (end) {
    params.EndDate = end;
    params.endDate = end;
  }

  const { data } = await apiClient.get(`/Teacher/history`, {
    params,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  let rows = [];
  if (Array.isArray(data)) rows = data;
  else if (Array.isArray(data?.data)) rows = data.data;

  // Backend may ignore date params — still bound the payload client-side when a range is given.
  if (start || end) {
    rows = filterHistoryByDateRange(rows, start || undefined, end || undefined);
  }
  return rows;
}

export function computeTeacherTimelineStats(rows, radiusMeters) {
  const days = new Set();
  let inRadius = 0;
  let outRadius = 0;
  (rows || []).forEach((row) => {
    const key = getAttendDateKey(row);
    if (key) days.add(key);
    const within = isWithinRadius(row, radiusMeters);
    if (within === true) inRadius += 1;
    else if (within === false) outRadius += 1;
  });
  return {
    checkIns: rows?.length || 0,
    uniqueDays: days.size,
    inRadius,
    outRadius,
  };
}

/** View modes available by map context. */
export function getAvailableViewModes(mapContext, checkInCount) {
  if (mapContext === MAP_CONTEXT.TEACHER_TIMELINE) {
    return VIEW_MODES.filter((m) => m.value !== "cluster");
  }
  if (mapContext === MAP_CONTEXT.SCHOOL_OVERVIEW) {
    if (checkInCount >= 5) return VIEW_MODES;
    return VIEW_MODES.filter((m) => m.value !== "cluster");
  }
  return VIEW_MODES.filter((m) => m.value !== "lines");
}

export function resolveEffectiveViewMode(viewMode, mapContext, checkInCount) {
  const available = getAvailableViewModes(mapContext, checkInCount).map((m) => m.value);
  if (available.includes(viewMode)) return viewMode;
  if (mapContext === MAP_CONTEXT.TEACHER_TIMELINE || mapContext === MAP_CONTEXT.SCHOOL_OVERVIEW) {
    return "lines";
  }
  return "markers";
}

export function defaultViewModeForContext(mapContext) {
  if (mapContext === MAP_CONTEXT.TEACHER_TIMELINE || mapContext === MAP_CONTEXT.SCHOOL_OVERVIEW) {
    return "lines";
  }
  return "markers";
}

export function buildHistoryPopup(row, labels) {
  const base = buildTeacherPopup(row, labels);
  const raw = row?.attendDateTime ?? row?.AttendDateTime ?? "";
  const dateLine = raw
    ? `<div class="map-popup-meta">${escapeHtml(labels.dateLabel || "Date")}: ${escapeHtml(String(raw).replace("T", " ").slice(0, 19))}</div>`
    : "";
  return base.replace('<div class="map-popup-meta">Status:', `${dateLine}<div class="map-popup-meta">Status:`);
}

function normalizeGeoName(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function geoNameMatches(optionText, targetName) {
  const a = normalizeGeoName(optionText);
  const b = normalizeGeoName(targetName);
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
}

function normalizeRegNumber(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/^0+/, "") || "0";
}

export function offsetCompareCoordinates(lat, lng) {
  const la = parseFloat(lat);
  const ln = parseFloat(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return [lat, lng];
  const offset = 0.0002;
  return [la + offset, ln + offset * 0.65];
}

export async function fetchTeacherAssignmentForMap(apiClient, teacherId, regNumberHint = "") {
  const id = String(teacherId);
  const token = localStorage.getItem("tk446655440000");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchSummary = async (params) => {
    const { data } = await apiClient.get("/Assignment/summary", { params, headers });
    return parseListPayload(data);
  };

  let teacherReg = String(regNumberHint ?? "").trim();
  let teacherName = "";
  if (!teacherReg) {
    try {
      const { data } = await apiClient.get("/Teacher/list", {
        params: { pageNumber: 1, pageSize: 5000 },
        headers,
      });
      const teachers = parseListPayload(data);
      const teacher = teachers.find((row) => String(row.id ?? row.Id ?? "") === id);
      if (teacher) {
        teacherReg = String(teacher.regNumber ?? teacher.RegNumber ?? "").trim();
        teacherName = [teacher.name, teacher.preName].filter(Boolean).join(" ").trim();
      }
    } catch {
      /* optional profile lookup */
    }
  } else {
    try {
      const { data } = await apiClient.get("/Teacher/list", {
        params: { pageNumber: 1, pageSize: 5000 },
        headers,
      });
      const teachers = parseListPayload(data);
      const teacher = teachers.find(
        (row) =>
          String(row.id ?? row.Id ?? "") === id ||
          String(row.regNumber ?? row.RegNumber ?? "").trim() === teacherReg
      );
      if (teacher) {
        teacherName = [teacher.name, teacher.preName].filter(Boolean).join(" ").trim();
      }
    } catch {
      /* optional profile lookup */
    }
  }

  const matchesTeacher = (row) => {
    const tid = String(row.teacherId ?? row.TeacherId ?? "").trim();
    if (tid && tid === id) return true;
    const rowReg = normalizeRegNumber(row.regNumber ?? row.RegNumber);
    const hintReg = normalizeRegNumber(teacherReg);
    if (hintReg && rowReg && rowReg === hintReg) return true;
    const rowName = [row.name, row.preName].filter(Boolean).join(" ").trim();
    if (teacherName && rowName && normalizeGeoName(rowName) === normalizeGeoName(teacherName)) {
      return true;
    }
    return false;
  };

  let rows = [];
  if (teacherReg) {
    rows = await fetchSummary({ regNumber: teacherReg, pageNumber: 1, pageSize: 20 });
    if (!rows.length) rows = await fetchSummary({ RegNumber: teacherReg, pageNumber: 1, pageSize: 20 });
    if (!rows.length) rows = await fetchSummary({ search: teacherReg, pageNumber: 1, pageSize: 20 });
    rows = rows.filter(matchesTeacher);
  }

  if (!rows.length && id) {
    rows = await fetchSummary({ search: id, pageNumber: 1, pageSize: 20 });
    rows = rows.filter(matchesTeacher);
  }

  if (!rows.length) {
    for (let page = 1; page <= 30; page += 1) {
      const chunk = await fetchSummary({ pageNumber: page, pageSize: 500 });
      if (!chunk.length) break;
      const hit = chunk.find((row) => {
        if (matchesTeacher(row)) return true;
        if (teacherReg && normalizeRegNumber(row.regNumber ?? row.RegNumber) === normalizeRegNumber(teacherReg)) {
          return true;
        }
        return false;
      });
      if (hit) {
        rows = [hit];
        break;
      }
      if (chunk.length < 500) break;
    }
  }

  const row = rows[0];
  if (!row) return null;

  const estabName = row.estName ?? row.EstName ?? row.establishment ?? row.Establishment ?? "";
  const estabCode = row.Code ?? row.code ?? "";
  let estabId =
    row.estabId ??
    row.EstabId ??
    row.estId ??
    row.EstId ??
    row.establishmentId ??
    row.EstablishmentId ??
    "";

  return {
    teacherId: id,
    estabId: String(estabId ?? ""),
    estabCode,
    estabName,
    provinceName: row.provinceName ?? row.ProvinceName ?? "",
    departmentName: row.districtName ?? row.DistrictName ?? row.departmentName ?? "",
    subprefectureName: row.spName ?? row.SpName ?? row.subprefectureName ?? "",
    teacherName: teacherName || [row.name, row.preName].filter(Boolean).join(" "),
    regNumber: teacherReg || row.regNumber || row.RegNumber || "",
  };
}

export async function resolveGeoIdsFromNames(axiosClient, baseUrl, getAuthHeaders, names) {
  const { data: pData } = await axiosClient.get(`${baseUrl}Assignment/provinces`, {
    headers: getAuthHeaders(),
  });
  const provinces = makeOptions(pData, ["provid", "id"], ["name", "province"]);
  const prov = provinces.find((p) => geoNameMatches(p.text, names.provinceName));
  if (!prov) return { provinces, depts: [], subps: [] };

  const { data: dData } = await axiosClient.get(`${baseUrl}Assignment/departments?ProvId=${prov.id}`, {
    headers: getAuthHeaders(),
  });
  const depts = makeOptions(dData, ["dptid", "id"], ["name", "department"]);
  const dept = depts.find((d) => geoNameMatches(d.text, names.departmentName));
  if (!dept) {
    return { provinceId: String(prov.id), departmentId: "", subprefectureId: "", provinces, depts, subps: [] };
  }

  const { data: sData } = await axiosClient.get(`${baseUrl}Assignment/subprefectures?DptId=${dept.id}`, {
    headers: getAuthHeaders(),
  });
  const subps = makeOptions(sData, ["sptid", "id"], ["name", "subprefecture"]);
  const subp = subps.find((s) => geoNameMatches(s.text, names.subprefectureName));

  return {
    provinceId: String(prov.id),
    departmentId: String(dept.id),
    subprefectureId: subp ? String(subp.id) : "",
    provinces,
    depts,
    subps,
  };
}

/** Walk geo tree until establishment id is found — used for map deep links. */
export async function resolveGeoChainForEstablishment(axiosClient, baseUrl, getAuthHeaders, estabId) {
  const targetId = String(estabId);
  if (!targetId) return null;

  const { data: pData } = await axiosClient.get(`${baseUrl}Assignment/provinces`, {
    headers: getAuthHeaders(),
  });
  const provinces = makeOptions(pData, ["provid", "id"], ["name", "province"]);

  for (const prov of provinces) {
    const { data: dData } = await axiosClient.get(
      `${baseUrl}Assignment/departments?ProvId=${prov.id}`,
      { headers: getAuthHeaders() }
    );
    const depts = makeOptions(dData, ["dptid", "id"], ["name", "department"]);

    for (const dept of depts) {
      const { data: sData } = await axiosClient.get(
        `${baseUrl}Assignment/subprefectures?DptId=${dept.id}`,
        { headers: getAuthHeaders() }
      );
      const subps = makeOptions(sData, ["sptid", "id"], ["name", "subprefecture"]);

      for (const subp of subps) {
        const { data: eData } = await axiosClient.get(
          `${baseUrl}Assignment/establishments?sptid=${subp.id}`,
          { headers: getAuthHeaders() }
        );
        const establishments = parseListPayload(eData)
          .map(normalizeEstablishment)
          .filter((e) => e.id);
        const hit = establishments.find((e) => String(e.id) === targetId);
        if (hit) {
          return {
            provinceId: String(prov.id),
            departmentId: String(dept.id),
            subprefectureId: String(subp.id),
            provinces,
            depts,
            subps,
            establishments,
          };
        }
      }
    }
  }

  return null;
}

/** Find geo chain by establishment name or code (when assignment has no estab id). */
export async function resolveGeoChainForEstablishmentName(
  axiosClient,
  baseUrl,
  getAuthHeaders,
  estabName,
  estabCode = ""
) {
  const target = normalizeGeoName(estabName);
  const code = String(estabCode ?? "").trim();
  if (!target && !code) return null;

  const { data: pData } = await axiosClient.get(`${baseUrl}Assignment/provinces`, {
    headers: getAuthHeaders(),
  });
  const provinces = makeOptions(pData, ["provid", "id"], ["name", "province"]);

  for (const prov of provinces) {
    const { data: dData } = await axiosClient.get(
      `${baseUrl}Assignment/departments?ProvId=${prov.id}`,
      { headers: getAuthHeaders() }
    );
    const depts = makeOptions(dData, ["dptid", "id"], ["name", "department"]);

    for (const dept of depts) {
      const { data: sData } = await axiosClient.get(
        `${baseUrl}Assignment/subprefectures?DptId=${dept.id}`,
        { headers: getAuthHeaders() }
      );
      const subps = makeOptions(sData, ["sptid", "id"], ["name", "subprefecture"]);

      for (const subp of subps) {
        const { data: eData } = await axiosClient.get(
          `${baseUrl}Assignment/establishments?sptid=${subp.id}`,
          { headers: getAuthHeaders() }
        );
        const establishments = parseListPayload(eData)
          .map(normalizeEstablishment)
          .filter((e) => e.id);
        const hit = establishments.find((e) => {
          if (target && geoNameMatches(e.name, estabName)) return true;
          if (code && String(e.code ?? e.Code ?? "").trim() === code) return true;
          return false;
        });
        if (hit) {
          return {
            provinceId: String(prov.id),
            departmentId: String(dept.id),
            subprefectureId: String(subp.id),
            establishmentId: String(hit.id),
            provinces,
            depts,
            subps,
            establishments,
          };
        }
      }
    }
  }

  return null;
}

export function createTimelineMarkerIcon(kind = "default", { selected = false, dayIndex = 0 } = {}) {
  const colors = {
    in: "#10b981",
    out: "#f97316",
    compare: "#8b5cf6",
    default: "#667eea",
  };
  const bg = colors[kind] || colors.default;
  const size = selected ? 18 : 14;
  const ring = selected ? "box-shadow:0 0 0 3px rgba(102,126,234,0.45);" : "";
  return L.divIcon({
    className: "map-teacher-dot-wrap",
    html: `<span class="map-teacher-dot map-teacher-dot--${kind}${selected ? " map-teacher-dot--selected" : ""}" style="width:${size}px;height:${size}px;background:${bg};${ring}">${dayIndex > 0 ? `<span class="map-teacher-dot-num">${dayIndex}</span>` : ""}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}
